import { MoodEmoji } from '~/components/mood-emoji'
import type { RangeDay } from '~/lib/api/types'
import styles from './mood-strip.module.scss'

type Props = {
  days: RangeDay[]
  todayIso: string
  userId: string
}

export function MoodStrip({ days, todayIso, userId }: Props) {
  return (
    <div className={styles.strip} role="img" aria-label="30-day mood pattern">
      {days.map(day => {
        const isToday = day.date === todayIso
        const cellLabel = describeDay(day, userId)
        return (
          <div
            key={day.date}
            className={`${styles.cell} ${isToday ? styles.today : ''}`}
            title={`${day.date} — ${cellLabel}`}
          >
            <DayGlyph day={day} userId={userId} />
            <span className={styles.date}>{shortDate(day.date)}</span>
          </div>
        )
      })}
    </div>
  )
}

function DayGlyph({ day, userId }: { day: RangeDay; userId: string }) {
  if (day.is_holiday) {
    return (
      <span className={styles.holiday} aria-hidden>
        ◇
      </span>
    )
  }
  const cell = day.cells.find(c => c.user_id === userId)
  if (!cell) {
    return (
      <span className={styles.dot} aria-hidden>
        ·
      </span>
    )
  }
  switch (cell.kind) {
    case 'working':
    case 'parse_failed':
      return <MoodEmoji mood={cell.mood} />
    case 'ooo':
      return (
        <span className={styles.ooo} aria-hidden>
          ○
        </span>
      )
    case 'missing':
    default:
      return (
        <span className={styles.dot} aria-hidden>
          ·
        </span>
      )
  }
}

function describeDay(day: RangeDay, userId: string): string {
  if (day.is_holiday)
    return `holiday: ${day.holiday?.name ?? 'company holiday'}`
  const cell = day.cells.find(c => c.user_id === userId)
  if (!cell) return 'no record'
  switch (cell.kind) {
    case 'working':
      return cell.mood ? `working (${cell.mood})` : 'working'
    case 'parse_failed':
      return 'check-in (parse failed)'
    case 'ooo':
      return `out: ${cell.ooo_kind}`
    case 'missing':
    default:
      return 'missing'
  }
}

function shortDate(iso: string): string {
  const [, m, d] = iso.split('-')
  return `${Number(m)}/${Number(d)}`
}
